import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ENVIRONMENT } from '@iot-platform/core';
import { ApiHelpers } from '@iot-platform/iot-platform-utils';
import { CommonApiListResponse, CommonApiRequestBody, Environment, LastValue, PlatformRequest, PlatformResponse, Record } from '@iot-platform/models/common';
import {
  Asset,
  AssetStatusName,
  AssetTemplateVariable,
  AssetVariable,
  AssetVariableCreationModel,
  Device,
  DeviceStatusName,
  DeviceVariable,
  Formula,
  I4BBulkOperationApiResponse,
  SourceVariable
} from '@iot-platform/models/i4b';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AssetVariablesService {
  private readonly environment: Environment = inject(ENVIRONMENT);
  public httpClient: HttpClient = inject(HttpClient);

  formulaResult$: Subject<LastValue[]> = new Subject<LastValue[]>();
  formulaResultForTemplateConfiguration$: Subject<{
    estimationRecord?: LastValue[];
    assetVariable: AssetTemplateVariable;
    error?: Error;
  }> = new Subject();
  cachedUnits$: BehaviorSubject<{ name: string; units: string[] }[]> = new BehaviorSubject<
    {
      name: string;
      units: string[];
    }[]
  >([]);

  getAll(request: PlatformRequest): Observable<PlatformResponse> {
    let params: HttpParams = new HttpParams();
    params = params.set('limit', request.limit.toString(10));
    params = params.set('page', request.page.toString(10));

    if (request.filters) {
      request.filters.forEach((filter) => {
        params = params.append(filter.criteriaKey, filter.value);
      });
    }

    return this.httpClient
      .get<AssetVariable[]>(`${this.environment.api.url}${this.environment.api.endpoints.assets}/${request.urlParam}/variables`, { params })
      .pipe(
        map((data: any) => ({
          data: data.content,
          currentPage: data.page.curPage,
          hasMore: data.page.hasMore,
          limit: data.page.limit,
          maxPage: data.page.maxPage,
          total: data.page.total
        }))
      );
  }

  getAssetsBySiteId(siteId: string, limit = 100, page = 0): Observable<Asset[]> {
    const body: CommonApiRequestBody = ApiHelpers.getHttpRequestBody({ page, limit, filters: [{ criteriaKey: 'siteId', value: siteId }] });
    return this.httpClient
      .post<CommonApiListResponse<Asset>>(`${this.environment.api.url + this.environment.api.endpoints.assets}/search`, body)
      .pipe(map((results) => results.content));
  }

  getDevicesBySiteId(siteId: string, limit = 100, page = 0): Observable<Device[]> {
    const body: CommonApiRequestBody = ApiHelpers.getHttpRequestBody({ page, limit, filters: [{ criteriaKey: 'siteId', value: siteId }] });
    return this.httpClient
      .post<CommonApiListResponse<Device>>(`${this.environment.api.url + this.environment.api.endpoints.devices}/search`, body)
      .pipe(map((results) => results.content));
  }

  getManyByAssetId(assetId: string, limit = 100, page = 0): Observable<AssetVariable[]> {
    return this.httpClient
      .get<
        CommonApiListResponse<AssetVariable>
      >(`${this.environment.api.url + this.environment.api.endpoints.assets}/${assetId}/variables?limit=${limit}&page=${page}`)
      .pipe(map((data) => data.content));
  }

  getDeviceVariablesByDeviceId(deviceId: string, limit = 3000, page = 0): Observable<DeviceVariable[]> {
    if (!deviceId) {
      return EMPTY;
    }
    return this.httpClient
      .get<any>(`${this.environment.api.url + this.environment.api.endpoints.devices}/${deviceId}/variables?limit=${limit}&page=${page}`)
      .pipe(map((data: any) => data.content));
  }

  getUnits(): Observable<{ name: string; units: string[] }[]> {
    return this.cachedUnits$.pipe(
      switchMap((cachedUnits) => {
        if (cachedUnits.length) {
          return of(cachedUnits);
        } else {
          return this.httpClient
            .get<
              {
                name: string;
                units: string[];
              }[]
            >(`${this.environment.api.url}${this.environment.api.endpoints.assetVariablesUnits}`)
            .pipe(tap((freshUnits) => this.cachedUnits$.next(freshUnits)));
        }
      })
    );
  }

  post(assetVariable: AssetVariableCreationModel): Observable<AssetVariable> {
    return this.httpClient.post<AssetVariable>(`${this.environment.api.url}${this.environment.api.endpoints.assetVariables}`, assetVariable);
  }

  put(assetVariable: AssetVariable): Observable<AssetVariable> {
    return this.httpClient.put<AssetVariable>(`${this.environment.api.url}${this.environment.api.endpoints.assetVariables}/${assetVariable.id}`, assetVariable);
  }

  updateThresholds(assetVariable: AssetVariable): Observable<AssetVariable> {
    return this.httpClient
      .patch<AssetVariable>(
        `${this.environment.api.url}${this.environment.api.endpoints.assetVariables}/${assetVariable.id}/thresholds`,
        assetVariable.thresholds.values.map((t) => ({ value: t.value, position: t.position }))
      )
      .pipe(map(() => assetVariable));
  }

  delete(assetVariable: AssetVariable): Observable<AssetVariable> {
    return this.httpClient
      .delete<any>(this.environment.api.url + this.environment.api.endpoints.assetVariables + '/' + assetVariable.id)
      .pipe(map(() => assetVariable));
  }

  bulkDelete(assetVariables: AssetVariable[]): Observable<AssetVariable>[] {
    return assetVariables.map((assetVariable) => this.delete(assetVariable));
  }

  deleteAssetVariables(assetVariablesIds: string[]): Observable<I4BBulkOperationApiResponse> {
    return this.httpClient.post<I4BBulkOperationApiResponse>(`${this.environment.api.url}${this.environment.api.endpoints.deleteAssetsVariables}`, {
      variablesIds: assetVariablesIds
    });
  }

  getDeviceVariableById(id: string): Observable<DeviceVariable> {
    return this.httpClient.get<DeviceVariable>(`${this.environment.api.url + this.environment.api.endpoints.deviceVariables}/${id}`);
  }

  getDeviceById(id: string): Observable<Device | null> {
    if (!id) {
      return of(null);
    }
    return this.httpClient.get<Device>(`${this.environment.api.url + this.environment.api.endpoints.devices}/${id}`);
  }

  getAssetVariableById(id: string): Observable<AssetVariable> {
    return this.httpClient.get<AssetVariable>(`${this.environment.api.url + this.environment.api.endpoints.assetVariables}/${id}`);
  }

  getAssetById(id: string): Observable<Asset> {
    return this.httpClient.get<Asset>(`${this.environment.api.url + this.environment.api.endpoints.assets}/${id}`);
  }

  // to be deleted
  calculateFormulaResult(data: { formula: Formula; records: Record[] }): void {
    this.httpClient.post(this.environment.api.url + this.environment.api.endpoints.assetVariablesCalculator, data).subscribe(
      (result: LastValue[]) => this.formulaResult$.next(result),
      () => this.formulaResult$.next(undefined)
    );
  }

  calculateFormula(data: { formula: Formula; records: Record[] }): Observable<LastValue | null> {
    return this.httpClient
      .post<LastValue[]>(this.environment.api.url + this.environment.api.endpoints.assetVariablesCalculator, data)
      .pipe(
        map(
          (lastValues) =>
            lastValues?.reduce((acc, val) => (!(acc?.value && acc?.datetime) ? val : new Date(val.datetime) > new Date(acc.datetime) ? val : acc), null) ?? null
        )
      );
  }

  calculateFormulaResultForTemplate(
    data: {
      formula: any;
      records: Record[];
    },
    assetVariable: AssetTemplateVariable
  ): void {
    this.httpClient.post(this.environment.api.url + this.environment.api.endpoints.assetVariablesCalculator, data).subscribe(
      (result: LastValue[]) =>
        this.formulaResultForTemplateConfiguration$.next({
          estimationRecord: result,
          assetVariable
        }),
      (error) => this.formulaResultForTemplateConfiguration$.next({ error, assetVariable })
    );
  }

  getGasList(): Observable<{ key: string; value: number }[]> {
    return this.httpClient.get<
      {
        key: string;
        value: number;
      }[]
    >(`${this.environment.api.url + this.environment.api.endpoints.assetVariables}` + '/densities');
  }

  resetAssetVariablesLastValues(assetVariablesIds: string[]): Observable<I4BBulkOperationApiResponse> {
    return this.httpClient.post<I4BBulkOperationApiResponse>(`${this.environment.api.url}${this.environment.api.endpoints.resetAssetVariablesLastValues}`, {
      variablesIds: assetVariablesIds
    });
  }

  breakFormula(assetVariableId: string): Observable<boolean> {
    return this.httpClient.post<boolean>(
      `${this.environment.api.url}${this.environment.api.endpoints.assetVariablesOperation}/${assetVariableId}${this.environment.api.endpoints.assetVariablesBreakFormula}`,
      null
    );
  }

  getCanConfigure(siteId: string): Observable<boolean> {
    return this.getDevicesBySiteId(siteId, 3000).pipe(map((devices) => devices.some((device) => device.status.name !== DeviceStatusName.decommissioned)));
  }

  getSources(siteId: string): Observable<SourceVariable[]> {
    if (!siteId) {
      return of([]);
    }

    return forkJoin([
      this.getAssetsBySiteId(siteId, 3000).pipe(
        map((assets: Asset[]) => assets?.filter((asset) => asset.status?.name !== AssetStatusName.decommissioned) ?? [])
      ),
      this.getDevicesBySiteId(siteId, 3000).pipe(
        map((devices: Device[]) => devices?.filter((device) => device.status?.name !== DeviceStatusName.decommissioned) ?? [])
      )
    ]).pipe(
      map(([assets, devices]) => [
        ...assets.map((asset) => ({ id: asset.id, name: asset.name, type: 'asset' as const })),
        ...devices.map((device) => ({ id: device.id, name: device.name, type: 'device' as const }))
      ])
    );
  }

  isNameUnique(name: string, assetId: string): Observable<boolean> {
    return this.getAll({
      limit: 0,
      page: 0,
      filters: [{ criteriaKey: 'name', value: name }],
      urlParam: assetId
    }).pipe(map((response: PlatformResponse) => (response.total > 0 ? false : true)));
  }
}
